// ZONE 3 · LATEST — what just happened.
//
// The newest entries of the merged stream (Layer 0 documents + Layer 1 events), ordered by
// the SAME comparator GET /api/timeline uses — the strip must never disagree with the page it
// links to. Rows expand onto their summary; the full stream is one route away.
//
// NEW comes from the payload (`entry.new`), counted from the operator's PREVIOUS visit, and
// the date it counts from is always stated. It is a delta, not a claim and not a state, so it
// takes ink weight — never a hue and never a claim-mark.
import { useState } from 'react';
import { useT, useLocale } from '../i18n/index.js';
import './ClientLatest.de.js';
import './ClientLatest.fr.js';
import type { BriefLatestEntry } from '../api/brief.js';

interface Props {
  latest: BriefLatestEntry[];
  latestSince: string;
  /** First visit: the server counts a 14-day window instead of a previous-visit date. */
  firstVisitWindow: boolean;
  onOpenTimeline: () => void;
  onOpenLibrary: () => void;
}

const SHOWN = 6;

export default function ClientLatest({ latest, latestSince, firstVisitWindow, onOpenTimeline, onOpenLibrary }: Props) {
  const t = useT();
  const { fmtDate } = useLocale();
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [all, setAll] = useState(false);
  const rows = all ? latest : latest.slice(0, SHOWN);
  const newCount = latest.filter((e) => e.new).length;

  const toggle = (ref: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref); else next.add(ref);
      return next;
    });

  return (
    <section className="zone" id="latest">
      <div className="zone-head">
        <span className="u-label">{t('Latest')}</span>
        <span className="zone-count voice-figure">
          {newCount
            ? (firstVisitWindow
              ? t('{n} new in the last 14 days', { n: newCount })
              : t('{n} new since {date}', { n: newCount, date: fmtDate(latestSince) }))
            : (firstVisitWindow
              ? t('nothing new in the last 14 days')
              : t('nothing new since {date}', { date: fmtDate(latestSince) }))}
          {latest.length > 0 && ` · ${t('newest {shown} of {total}', { shown: rows.length, total: latest.length })}`}
        </span>
        <button type="button" className="zone-route" onClick={onOpenLibrary}>{t('Library')} →</button>
        <button type="button" className="zone-route" style={{ marginLeft: 0 }} onClick={onOpenTimeline}>
          {t('Full timeline')} →
        </button>
      </div>

      {!latest.length ? (
        <p className="zone-empty">
          {t('Nothing on the record yet — no document ingested, no timeline event written.')}
        </p>
      ) : (
        <div className="sheet sheet--clip">
          {rows.map((entry) => {
            const isOpen = open.has(entry.ref);
            return (
              <div key={entry.ref}>
                <button
                  type="button"
                  className={`client-lrow${entry.new ? ' client-lrow--new' : ''}${isOpen ? ' client-lrow--open' : ''}`}
                  onClick={() => toggle(entry.ref)}
                >
                  <span className="client-ldate voice-figure">{fmtDate(entry.date)}</span>
                  <span className="client-lkind">{t(entry.kind ?? entry.layer)}</span>
                  <span className="client-ltitle">{entry.title}</span>
                  {entry.superseded_by_ref && (
                    <span className="client-chip">{t('superseded by')} <span className="src-ref">{entry.superseded_by_ref}</span></span>
                  )}
                  <span className="client-chip src-ref">{entry.ref}</span>
                </button>
                {isOpen && (
                  <div className="client-lsum">
                    {entry.summary
                      ? <span className="read">{entry.summary}</span>
                      : <span className="n-none">{t('No summary on record.')}</span>}
                  </div>
                )}
              </div>
            );
          })}
          {latest.length > SHOWN && (
            <button type="button" className="client-qtoggle" onClick={() => setAll((v) => !v)}>
              {all ? '▾' : '▸'} {all ? t('show the newest six') : t('{n} older on this strip', { n: latest.length - SHOWN })}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
